import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaGripVertical,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const ICON_OPTIONS = [
  { value: 'users-team', label: 'Expert Team' },
  { value: 'circular-arrows', label: 'Circular Arrows' },
  { value: 'expand-scale', label: 'Expand / Scale' },
  { value: 'crosshair-target', label: 'Target' },
  { value: 'sun-pricing', label: 'Sun / Pricing' },
  { value: 'chat-support', label: 'Chat / Support' },
];

const emptyCard = (index) => ({
  cardNumber: String(index + 1).padStart(2, '0'),
  icon: 'users-team',
  cardTitle: '',
  cardDescription: '',
  cardColor: 'blue',
  isActive: true,
  order: index,
});

const WhyBooksComplyCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    badge: '',
    title: '',
    description: '',
    isPublished: true,
    cards: [],
  });

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/why-bookscomply/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data;
        setForm({
          badge: data.badge || '',
          title: data.title || '',
          description: data.description || '',
          isPublished: data.isPublished !== false,
          cards: (data.cards || []).map((card, index) => ({
            _id: card._id,
            cardNumber: card.cardNumber || String(index + 1).padStart(2, '0'),
            icon: card.icon || 'users-team',
            cardTitle: card.cardTitle || '',
            cardDescription: card.cardDescription || '',
            cardColor: card.cardColor || 'blue',
            isActive: card.isActive !== false,
            order: typeof card.order === 'number' ? card.order : index,
          })),
        });
      }
    } catch (err) {
      toastError('Failed to load Why BooksComply content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateCard = (index, field, value) => {
    setForm((prev) => {
      const cards = [...prev.cards];
      cards[index] = { ...cards[index], [field]: value };
      return { ...prev, cards };
    });
  };

  const addCard = () => {
    setForm((prev) => ({
      ...prev,
      cards: [...prev.cards, emptyCard(prev.cards.length)],
    }));
  };

  const removeCard = (index) => {
    if (!window.confirm('Remove this card?')) return;
    setForm((prev) => ({
      ...prev,
      cards: prev.cards
        .filter((_, i) => i !== index)
        .map((card, i) => ({
          ...card,
          order: i,
          cardNumber: card.cardNumber || String(i + 1).padStart(2, '0'),
        })),
    }));
  };

  const moveCard = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.cards.length) return;
    setForm((prev) => {
      const cards = [...prev.cards];
      [cards[index], cards[target]] = [cards[target], cards[index]];
      return {
        ...prev,
        cards: cards.map((card, i) => ({ ...card, order: i })),
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/why-bookscomply`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Why BooksComply page saved successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="Why BooksComply"
        description="Manage the public Why BooksComply page content"
        icon={FaGlobe}
        actions={
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-premium flex items-center gap-2"
          >
            <FaSave className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        }
      />

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page Header</h2>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) => updateField('isPublished', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Published
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Badge</label>
            <input
              className="input-modern"
              value={form.badge}
              onChange={(e) => updateField('badge', e.target.value)}
              placeholder="Why BooksComply"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Title</label>
            <input
              className="input-modern"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="Leave empty for default gradient title"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Description</label>
          <textarea
            className="input-modern min-h-[100px]"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Page description"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Feature Cards</h2>
          <button onClick={addCard} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-colors">
            <FaPlus className="w-3.5 h-3.5" /> Add Card
          </button>
        </div>

        {form.cards.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No cards yet. Click &quot;Add Card&quot; to create one.</p>
        ) : (
          <div className="space-y-5">
            {form.cards.map((card, index) => (
              <div
                key={card._id || `card-${index}`}
                className={`rounded-2xl border p-5 space-y-4 transition-opacity ${
                  card.isActive
                    ? 'border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-900/30'
                    : 'border-gray-200 dark:border-dark-700 bg-gray-100/50 dark:bg-dark-900/10 opacity-70'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FaGripVertical className="text-gray-400" />
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                      Card {index + 1}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        card.isActive
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-gray-200 text-gray-600 dark:bg-dark-700 dark:text-gray-400'
                      }`}
                    >
                      {card.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveCard(index, -1)}
                      disabled={index === 0}
                      className="px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-dark-600 disabled:opacity-40"
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCard(index, 1)}
                      disabled={index === form.cards.length - 1}
                      className="px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-dark-600 disabled:opacity-40"
                    >
                      Down
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCard(index, 'isActive', !card.isActive)}
                      className="p-2 text-gray-500 hover:text-primary-600 rounded-lg transition-colors"
                      title={card.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {card.isActive ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeCard(index)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete Card"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Card Number</label>
                    <input
                      className="input-modern"
                      value={card.cardNumber}
                      onChange={(e) => updateCard(index, 'cardNumber', e.target.value)}
                      placeholder="01"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Icon</label>
                    <select
                      className="input-modern"
                      value={card.icon}
                      onChange={(e) => updateCard(index, 'icon', e.target.value)}
                    >
                      {ICON_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Card Color</label>
                    <select
                      className="input-modern"
                      value={card.cardColor}
                      onChange={(e) => updateCard(index, 'cardColor', e.target.value)}
                    >
                      <option value="blue">Blue</option>
                      <option value="orange">Orange</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Card Title</label>
                  <input
                    className="input-modern"
                    value={card.cardTitle}
                    onChange={(e) => updateCard(index, 'cardTitle', e.target.value)}
                    placeholder="Card title"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Card Description</label>
                  <textarea
                    className="input-modern min-h-[90px]"
                    value={card.cardDescription}
                    onChange={(e) => updateCard(index, 'cardDescription', e.target.value)}
                    placeholder="Plain text or HTML (e.g. links with &lt;a href=&quot;/bookkeeping&quot;&gt;)"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-premium flex items-center gap-2"
        >
          <FaSave className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </PageLayout>
  );
};

export default WhyBooksComplyCMS;
